/* eslint-disable import/first */
/**
 * Entry point for the React app on the client side
 */

import 'public-path';
import 'helpers/ie11-classlist-polyfill';
import React from 'react';
import ReactDOM from 'react-dom';
import { loadableReady } from '@loadable/component';
import DAL from '@classpass/dal';
import AjaxClient from '@classpass/dal/ajax-client';
import { Provider as ReduxProvider } from 'react-redux';
import { IntlProvider, addLocaleData } from 'react-intl';
import { Router, match, browserHistory } from 'react-router';

import publicEndpoints from 'endpoints';
import clientConfig from 'universal-config';
import createStore from 'redux/create';
import { sagaErrorHandler } from 'redux/sagaErrorHandler';
import { setStore } from 'store';
import { ClientProvider } from 'providers/ClientProvider';
import { defaultLocaleKey } from 'dynamic-data/supported-locales.constants';
import supportedLocales from 'locales/supported-locales.json';
import { setLocale } from 'redux/modules/locale';
import { selectIsPrelaunchMode } from 'redux/modules/page';
import { setRequestDetailsOnClient } from 'redux/modules/request';
import { setDeviceType, setUserId } from 'helpers/multitrack';
import { isMobileDevice } from 'helpers/isMobile';
import { transportErrorHandler } from 'helpers/http';
import routes from 'routes';

const transport = new AjaxClient(
  {
    token: clientConfig().unauthenticatedToken,
  },
  `${window.location.protocol}//${window.location.host}/_api`
);

transport.on('failure', transportErrorHandler);

const client = new DAL({ transport, endpoints: publicEndpoints });
client.setUnauthenticatedToken(clientConfig().unauthenticatedToken);
client.setHeader('platform', clientConfig().platform);

if (__LOCAL_DEV__) {
  window._client = client; // eslint-disable-line no-underscore-dangle
}

const storeData = JSON.parse(document.getElementById('store').textContent);
const { store, history } = createStore(browserHistory, storeData, {
  onError: sagaErrorHandler,
});

// Check if the server has rendered in prelaunch mode
const isPrelaunchMode = selectIsPrelaunchMode(store.getState());
if (isPrelaunchMode) {
  // Set a prelaunch header so upstream services can handle the request correctly.
  client.setHeader('X-CPPrelaunchAccess', true);
}

setStore(store);

if (__LOCAL_DEV__) {
  /* eslint-disable no-console */

  const logSuccess = (onServer, event) => {
    if (event.params && Object.keys(event.params).length) {
      console.info(
        `%c[API${onServer ? ' (server)' : ''}] %c${event.method}%c to %c${event.name} (${event.url})%c took %c${
          event.duration
        }ms%c with %O`,
        'font-weight: bold',
        'color:purple',
        '',
        'color:green',
        '',
        'color:blue',
        '',
        event.params
      );
    } else {
      console.info(
        `%c[API${onServer ? ' (server)' : ''}] %c${event.method}%c to %c${event.name} (${event.url})%c took %c${
          event.duration
        }ms`,
        'font-weight: bold',
        'color:purple',
        '',
        'color:green',
        '',
        'color:blue'
      );
    }
  };

  const logFailure = (onServer, event) => {
    if (event.params && Object.keys(event.params).length) {
      console.info(
        `%c[API${onServer ? ' (server)' : ''}] %cFAILURE ${event.code || event.httpStatus} %c${event.method}%c to %c${
          event.name
        } (${event.url})%c took %c${event.duration}ms%c with %O`,
        'font-weight: bold',
        'color: red; font-weight: bold',
        'color:purple',
        '',
        'color:green',
        '',
        'color:blue',
        '',
        event.params
      );
    } else {
      console.info(
        `%c[API${onServer ? ' (server)' : ''}] %cFAILURE ${event.code || event.httpStatus} %c${event.method}%c to %c${
          event.name
        } (${event.url})%c took %c${event.duration}ms`,
        'font-weight: bold',
        'color: red; font-weight: bold',
        'color:purple',
        '',
        'color:green',
        '',
        'color:blue'
      );
    }
  };

  const requestLogNode = document.getElementById('api-requests');
  if (requestLogNode) {
    const apiRequestsMadeOnServer = JSON.parse(requestLogNode.textContent);
    apiRequestsMadeOnServer.forEach(([type, event]) => {
      if (type === 'success') {
        logSuccess(true, event);
      } else {
        logFailure(true, event);
      }
    });
  }

  transport.on('success', logSuccess.bind(this, false));
  transport.on('failure', logFailure.bind(this, false));

  /* eslint-enable no-console */
}

if (!storeData.locale) {
  store.dispatch(setLocale(supportedLocales[0]));
}

if (store.getState().user.token) {
  client.useToken(store.getState().user.token);
}

if (storeData.entities.userProfile.data.default) {
  setUserId(storeData.entities.userProfile.data.default.id);
}
setDeviceType(isMobileDevice() ? 'phone' : 'desktop');

const routerLocation = history.createLocation(window.location.pathname + window.location.search);

let currentLocation;

history.listen(
  (function historyListener(location) {
    if (window.ga) {
      window.ga('set', 'page', location.pathname);
    }
    store.dispatch(setRequestDetailsOnClient());

    const prevUrl = currentLocation ? currentLocation.pathname + currentLocation.search : null;
    const currentUrl = location.pathname + location.search;

    if (prevUrl !== currentUrl) {
      const event = document.createEvent('Event');
      event.previousUrl = currentLocation ? currentLocation.pathname : null;
      event.currentUrl = location.pathname;
      event.initEvent('historyChanged', true, true);
      document.body.dispatchEvent(event);
    }

    currentLocation = location;
    return historyListener;
  })(routerLocation)
);

const onReactRouterError = error => {
  console.error('React Router onError', error); // eslint-disable-line no-console
  if (window.Rollbar) {
    window.Rollbar.error(error);
  }
};

const { code: localeCode, messages, formatData } = window._locale || {}; // eslint-disable-line no-underscore-dangle

addLocaleData(formatData);

client.setHeader('Accept-Language', localeCode || supportedLocales[0]);

match(
  {
    routes: routes(store, client),
    location: routerLocation,
    history,
  },
  (err, redirectLocation, renderProps) => {
    if (err) {
      onReactRouterError(err);
    }

    // DANGER WILL ROBINSON: history= must come AFTER renderProps
    // so it clobbers renderProps.history
    const component = <Router {...renderProps} onError={onReactRouterError} history={history} />;

    loadableReady(() => {
      const root = document.getElementById('content');

      ReactDOM.render(
        <IntlProvider defaultLocale={defaultLocaleKey} locale={localeCode || 'en-US'} messages={messages}>
          <ReduxProvider store={store} key="provider">
            <ClientProvider client={client}>{component}</ClientProvider>
          </ReduxProvider>
        </IntlProvider>,
        root
      );
    });
  }
);
